/**
 * Zod схемүүд — core, sync, media, web, collector, handheld БҮГД эндээс.
 *
 * ⚠️ Сервис эсвэл апп дотор inline `z.object({...})` бичихийг хориглоно.
 * Sync-ийн алдааны дийлэнх нь энэ пакетаас шийдэгддэг.
 */
import { z } from 'zod';
// ============================================================
//  Enum — schema.prisma-тай ЯГ таарах ёстой
// ============================================================
export const UserRole = z.enum([
    'ADMIN', 'DIRECTOR_READONLY', 'WAREHOUSE',
    'STORE_MANAGER', 'SALESPERSON', 'COLLECTOR', 'ACCOUNTANT',
]);
export const StoreType = z.enum(['WAREHOUSE', 'RETAIL']);
export const LocationType = z.enum([
    'SUPPLIER', 'TRANSIT_INTL', 'WAREHOUSE_INTAKE', 'WAREHOUSE_MAIN',
    'TRANSIT_INTERNAL', 'SALES_FLOOR', 'BACK_ROOM', 'DISPLAY',
    'REPAIR', 'QUARANTINE', 'CUSTOMER', 'DISPOSED',
]);
export const MovementReason = z.enum([
    'COLLECT', 'SHIP', 'RECEIVE', 'LABEL', 'TRANSFER',
    'SELL', 'RETURN', 'REPAIR', 'WRITE_OFF', 'ADJUST',
]);
export const DeviceType = z.enum([
    'COLLECTOR_PHONE', 'HANDHELD', 'POS', 'LABEL_PRINTER', 'WEB',
]);
export const SupplierType = z.enum(['INDIVIDUAL', 'COMPANY']);
export const LookupCategory = z.enum([
    'COLORWAY', 'DEFECT_TYPE', 'RETURN_REASON',
    'WRITE_OFF_REASON', 'CLAIM_REJECT_REASON',
    // Зээлийн / түрийвчний апп — Simple, LendMN, Sono… Зах зээлд шинэ
    // апп байнга гардаг тул жагсаалт нь DB-д, захиалагчийн мэдэлд.
    'PAYMENT_PROVIDER',
    // Кассын урамшуулал — «Цахилгаан бараа» гэх мэт. Кассчин анх шивж
    // зарахад үүснэ (`OrderConfirmInput.promotion`).
    'PROMOTION',
]);
export const Material = z.enum(['REINDEER', 'LEATHER', 'FELT', 'OTHER']);
export const Gender = z.enum(['MEN', 'WOMEN', 'UNISEX', 'KIDS']);
export const PurchaseStatus = z.enum(['DRAFT', 'SYNCED', 'COSTED', 'RECEIVED']);
export const CostAllocation = z.enum(['BY_VALUE', 'BY_UNIT']);
/** ⚠️ IN_STORE / IN_WAREHOUSE / IN_TRANSFER БАЙХГҮЙ — байршил Location-оос. */
export const ItemStatus = z.enum([
    'COLLECTED', 'AVAILABLE', 'RESERVED', 'SOLD',
    'RETURNED', 'DEFECTIVE', 'WRITTEN_OFF',
]);
export const TransferStatus = z.enum(['DRAFT', 'IN_TRANSIT', 'RECEIVED', 'PARTIAL']);
export const StockCountStatus = z.enum(['OPEN', 'CLOSED']);
export const ReservationStatus = z.enum(['ACTIVE', 'FULFILLED', 'EXPIRED', 'CANCELLED']);
export const PhotoRole = z.enum(['PRIMARY', 'DETAIL', 'SOLE', 'DEFECT', 'INVOICE']);
export const OrderStatus = z.enum(['ACTIVE', 'VOIDED']);
export const SaleChannel = z.enum(['STORE', 'ONLINE', 'WHOLESALE']);
export const PaymentMethod = z.enum([
    'CASH', 'CARD', 'QPAY', 'TRANSFER', 'VOUCHER', 'MIXED', 'EXTERNAL_POS',
    // Аль апп болохыг `order_payments.provider_id` хэлнэ — шинэ апп
    // гарах бүрд enum өөрчлөхгүй (захиалагч вэбээс нэмнэ).
    'CREDIT_APP',
    // Хувь зээл — дэлгүүрийн өөрийн зээл. `@boots/domain` `installment.ts`.
    'INSTALLMENT',
]);
export const ReturnDisposition = z.enum([
    'RESTOCKED', 'DEFECTIVE', 'RETURN_TO_SUPPLIER', 'WRITTEN_OFF',
]);
/**
 * Буцаалтын төрөл (2026-10-03): REFUND — төлбөрийг бүтнээр буцаах,
 * EXCHANGE — солилцоо, зөрүүг тооцно.
 */
export const ReturnType = z.enum(['REFUND', 'EXCHANGE']);
/**
 * Худалдан авагчид мөнгийг ЯМАР хэлбэрээр буцаасан. Зээлийн апп, хувь
 * зээл, гадны касс руу буцаахгүй — бодит мөнгөний 4 хэлбэр л.
 * ⚠️ `PaymentMethod`-ийн `EXCHANGE`-ийг энд оруулахгүй: тэр нь мөнгө биш.
 */
export const RefundMethod = z.enum(['CASH', 'CARD', 'QPAY', 'TRANSFER']);
export const ClaimResolution = z.enum([
    'PENDING', 'REPAIR', 'EXCHANGE', 'REFUND', 'REJECTED',
]);
export const RenditionStatus = z.enum([
    'PENDING', 'PROCESSING', 'READY', 'FAILED',
    /** Баталгаа дуусаад 6 сар — бүх хувилбар устсан (media `photo:purge`). */
    'PURGED',
]);
// ============================================================
//  Суурь төрлүүд
// ============================================================
export const Uuid = z.string().uuid();
/** Мөнгө — string-ээр дамжина. JS Number алдаа гаргана. */
export const MoneyString = z.string().regex(/^-?\d+(\.\d{1,2})?$/, {
    message: 'Мөнгө "123456.78" хэлбэртэй string байх ёстой',
});
/** Ханш — 6 хүртэл орон */
export const RateString = z.string().regex(/^\d+(\.\d{1,6})?$/);
/** Гутлын код — 11–12 оронтой баркод эсвэл хуучин 10 оронтой (хэлбэрийг domain шалгана) */
export const Serial = z.string().regex(/^\d{10,12}$/, {
    message: 'Гутлын код 10–12 оронтой тоо байх ёстой',
});
/** Утас — нормчилсон хэлбэр */
export const PhoneMn = z.string().regex(/^\+976\d{8}$/, {
    message: 'Утас +976XXXXXXXX хэлбэртэй байх ёстой',
});
export const Size = z.number().int().min(20).max(60);
export const IsoDate = z.string().datetime({ offset: true });
export const Pagination = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
export const IdParam = z.object({ id: Uuid });
// ============================================================
//  Auth
// ============================================================
export const LoginInput = z.object({
    username: z.string().min(3).max(50),
    password: z.string().min(6).max(200),
});
/** 6 оронтой TOTP код. */
const TotpCode = z.string().regex(/^\d{6}$/, 'TOTP код 6 оронтой байна');
/**
 * DIRECTOR_READONLY-д хоёр дахь алхам.
 *
 * ⚠️ `challengeId` нь UUID БИШ — stateless JWT (`typ: 'totp'`). Сервер
 *    талд challenge хүснэгт үүсгэхгүйгээр container restart-ыг давна.
 */
export const TotpInput = z.object({
    challengeId: z.string().min(20),
    code: TotpCode,
});
export const ChangePasswordInput = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: z.string().min(8, 'Шинэ нууц үг дор хаяж 8 тэмдэгт').max(200),
});
/** Нэвтэрсэн хэрэглэгчийн товч мэдээлэл — passwordHash / totpSecret ХЭЗЭЭ Ч орохгүй. */
export const AuthUserOutput = z.object({
    id: Uuid,
    username: z.string(),
    fullName: z.string(),
    /**
     * Ажилтны зураг — БОГИНО хугацаатай presigned GET (10 минут).
     *
     * ⚠️ Хадгалахгүй, дахин ашиглахгүй: хугацаа дуусвал зураг ачаалагдахаа
     *    болино, апп үсгэн дугуйг харуулна. Шинэ холбоос нь дараагийн
     *    нэвтрэлт / refresh-д ирнэ.
     * ⚠️ Хуучин сервертэй апп ярьж болно (APK шууд суулгадаг) тул
     *    `optional` — талбар дутуу ирэх нь алдаа БИШ.
     */
    avatarUrl: z.string().nullable().optional(),
    role: UserRole,
    storeId: Uuid.nullable(),
    storeName: z.string().nullable(),
    isActive: z.boolean(),
    /**
     * UI-д зориулсан эрхийн жагсаалт — `"модуль.үйлдэл"` хэлбэртэй.
     *
     * ⚠️ ХАМГААЛАЛТ БИШ — цэс, товч нуухад л ашиглана. Сервер эрхийг
     *    хүсэлт бүрд өөрөө шалгана (`requirePermission`), тул энэ
     *    жагсаалтыг клиент талд өөрчилсөн ч юу ч нээгдэхгүй.
     */
    permissions: z.array(z.string()),
});
export const LoginOutput = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    user: AuthUserOutput,
});
/**
 * TOTP тохируулсан хэрэглэгч нэвтрэхэд токены оронд ЭНЭ ирнэ.
 *
 * Алдаа БИШ, 200. `ErrorCode`-д `TOTP_REQUIRED` нэмбэл бүх сервис болон
 * `docs/API.md`-ийн алдааны хүснэгтийн контракт өөрчлөгдөнө — оронд нь
 * discriminated body ашиглана.
 */
export const TotpChallengeOutput = z.object({
    totpRequired: z.literal(true),
    challengeId: z.string(),
    expiresIn: z.number().int(),
});
/** `POST /auth/login`-ийн хариу — хоёрын нэг. */
export const LoginResult = z.union([LoginOutput, TotpChallengeOutput]);
/**
 * ADMIN хэрэглэгчид TOTP тохируулах эхний алхам.
 * Нууц нь `enrollToken` дотор л явна — код баталгаажих хүртэл DB-д БИЧИГДЭХГҮЙ.
 */
export const TotpEnrollOutput = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
    enrollToken: z.string(),
});
export const TotpConfirmInput = z.object({
    enrollToken: z.string().min(20),
    code: TotpCode,
});
// ============================================================
//  Лавлах — жагсаалтын шүүлтүүр
// ============================================================
/** Zod нь "true"/"false" string-ийг boolean болгоно (query string-д бүгд string). */
const BoolQuery = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((v) => v === true || v === 'true' || v === '1');
export const LocationListQuery = z.object({
    storeId: Uuid.optional(),
    sellable: BoolQuery.optional(),
    countable: BoolQuery.optional(),
    active: BoolQuery.optional(),
});
export const LookupListQuery = z.object({
    category: LookupCategory.optional(),
    active: BoolQuery.optional(),
});
export const ModelListQuery = z.object({
    material: Material.optional(),
    gender: Gender.optional(),
    active: BoolQuery.optional(),
});
/**
 * Хуваарьт ажлын нэр — `POST /system/jobs/:name/trigger`.
 *
 * ⚠️ `:` тэмдэгт агуулдаг (`views:refresh`) тул UUID биш. Сервер нь
 *    мэдэгдэж буй жагсаалттай тулгаж шалгана — энд зөвхөн хэлбэр.
 */
export const JobNameParam = z.object({
    name: z.string().regex(/^[a-z]+:[a-z-]+$/, 'Ажлын нэр "бүлэг:үйлдэл" хэлбэртэй'),
});
/** Захирлын ханшийн мэдээлэл — гараар шинэчлэх тугтай. */
export const FxLiveQuery = z.object({
    refresh: z.enum(['0', '1']).optional(),
});
export const FxLatestQuery = z.object({
    currency: z.string().length(3).default('RUB'),
});
/** Дашбоардын багана диаграмын хугацааны нэгж. */
export const DashboardPeriod = z.enum(['week', 'month', 'quarter']);
export const DashboardQuery = z.object({
    period: DashboardPeriod.default('month'),
});
/**
 * «Нөөц ба байршил» — `GET /reports/stock-locations` (захиалагчийн
 * цэгцлэл №5): нийт · салбар (`RETAIL`) · агуулах (`WAREHOUSE`).
 */
export const StockScope = z.enum(['all', 'retail', 'warehouse']);
export const StockLocationsQuery = z.object({
    scope: StockScope.default('all'),
});
/** Салбарын самбар. `storeId` өгөөгүй бол өөрийн (эсвэл хариуцсан эхний) салбар. */
export const BranchDashboardQuery = z.object({
    storeId: Uuid.optional(),
});
// ============================================================
//  Sync — Орос дахь аппын протокол
// ============================================================
/**
 * Багц илгээх. ЗАРЧИМ:
 *  - id нь клиент дээр үүссэн UUID v7
 *  - Сервер `ON CONFLICT (id) DO NOTHING` хийнэ — давхардлын түлхүүр
 *    нь тэр `id`. `Idempotency-Key` header БАЙХГҮЙ
 *  - Зураг metadata эхлээд, файл presigned URL-аар дараа
 */
export const SyncPurchaseInput = z.object({
    id: Uuid,
    supplierId: Uuid,
    collectorId: Uuid,
    purchasedAt: IsoDate,
    currency: z.string().length(3).default('RUB'),
    goodsAmount: MoneyString,
    notes: z.string().max(2000).optional(),
});
export const SyncItemInput = z.object({
    id: Uuid,
    purchaseId: Uuid,
    modelId: Uuid,
    supplierId: Uuid,
    size: Size,
    purchaseAmount: MoneyString,
    currency: z.string().length(3).default('RUB'),
    collectedAt: IsoDate,
    warrantyDaysOverride: z.number().int().positive().nullable().optional(),
});
export const SyncPhotoInput = z.object({
    id: Uuid,
    itemId: Uuid.nullable().optional(),
    purchaseId: Uuid.nullable().optional(),
    role: PhotoRole.default('DETAIL'),
    sortOrder: z.number().int().min(0).default(0),
    sha256: z.string().length(64),
    bytes: z.number().int().positive(),
    capturedAt: IsoDate.optional(),
});
export const SyncPushInput = z.object({
    deviceId: Uuid,
    purchases: z.array(SyncPurchaseInput).max(50),
    items: z.array(SyncItemInput).max(1000),
    photos: z.array(SyncPhotoInput).max(5000),
});
export const SyncRejectOutput = z.object({
    id: Uuid,
    errorCode: z.enum(['VALIDATION', 'FK_MISSING', 'DUPLICATE', 'DEVICE_BLOCKED']),
    detail: z.string().optional(),
});
export const SyncPushOutput = z.object({
    batchId: Uuid,
    accepted: z.number().int(),
    rejected: z.number().int(),
    rejects: z.array(SyncRejectOutput),
    uploadUrls: z.array(z.object({ photoId: Uuid, url: z.string().url() })),
});
// ============================================================
//  Бараа
// ============================================================
export const ItemCreateInput = z.object({
    purchaseId: Uuid,
    modelId: Uuid,
    supplierId: Uuid,
    /**
     * ⚠️ Заавал БИШ (2026-09-05). Орост олноор бүртгэхэд ажилтан зөвхөн
     *    өнгө, хүйс, үнийг өгнө — размерыг агуулах уншуулах үедээ
     *    бөглөнө. Загварын размерын хязгаарын шалгалт мөн тэр үед л
     *    хийгдэнэ (`classifyItemLine`).
     */
    size: Size.optional(),
    purchaseAmount: MoneyString,
    currency: z.string().length(3).default('RUB'),
    collectedAt: IsoDate,
    /**
     * Нийлүүлэгчээс ЗЭЭЛЭЭР авсан эсэх — мөнгө хараахан төлөгдөөгүй.
     *
     * Заавал биш; өгөөгүй бол `false` (бэлнээр авсан). Цуглуулагч нэг
     * аялалд заримыг нь бэлнээр, заримыг нь зээлээр авдаг тул энэ нь
     * аялалын биш ГУТЛЫН шинж чанар.
     */
    isCredit: z.boolean().optional(),
});
// ---- Багц үүсгэх — POST /items/batch. Бараа үүсгэх ЦОРЫН ГАНЦ зам ----
/**
 * Багцын нэг мөр.
 *
 * ⚠️ `id` нь КЛИЕНТ дээр UUID v7-оор үүснэ (CLAUDE.md — Орост UUID).
 *    Давхардлын түлхүүр нь ЭНЭ — `items.id` PK тул DB түвшинд
 *    баталгаажна. Дахин илгээхэд ижил `id` = ижил гутал.
 */
export const ItemBatchLine = ItemCreateInput.extend({
    id: Uuid,
    warrantyDaysOverride: z.number().int().positive().max(3650).nullable().optional(),
    /**
     * Орост хэвлэгдсэн ТҮР баркод — 10 цифр, `9` угтвартай.
     *
     * ⚠️ Серийн дугаар БИШ (дүрэм 6 хэвээр). Гутлан дотор явж, агуулах
     *    уншуулмагц жинхэнэ дугаар олгогдоно. Формат/Luhn шалгалт нь
     *    handler-т (`isTempCode`, `@boots/domain`) — жагсаалтыг schema
     *    руу давтахгүй.
     */
    tempCode: z.string().length(10).optional(),
    /**
     * Орост УТАС өөрөө олгосон ЖИНХЭНЭ серийн дугаар.
     *
     * ⚠️ Клиентэд итгэхгүй. Сервер үүнийг тухайн ТӨХӨӨРӨМЖИД олгогдсон
     *    муж дотор байгаа эсэхийг шалгана (`serial_blocks`) — эс тэгвээс
     *    алдаатай апп өөр хүний дугаарыг нэхэмжилж, дараа нь жинхэнэ
     *    эзэнтэй нь мөргөлдөнө. Формат/Luhn нь `Serial`-д.
     */
    serial: Serial.optional(),
});
/**
 * Агуулахын хүлээн авалт — Орост хэвлэгдсэн ТҮР баркодыг уншуулна.
 *
 * `storeId` заагаагүй бол ажилтны үндсэн агуулах. `locationId`
 * заагаагүй бол тухайн агуулахын `WAREHOUSE_INTAKE`.
 */
/**
 * Эхний үлдэгдэл — шошготой байсан гутлыг POS-оор нэг удаа бүртгэх
 * (`POST /items/opening`). Нийлүүлэгч, размер, огноо баркодоос.
 *
 * `id` — клиентийн UUID v7, идемпотентлэг: сүлжээ тасарч дахин
 * илгээсэн ч гутал хоёр үүсэхгүй (`items` PK).
 */
export const OpeningItemInput = z.object({
    id: Uuid,
    barcode: z.string().min(8).max(24),
    storeId: Uuid,
    /**
     * Зарах үнэ — ЗААВАЛ БИШ (2026-10-03). Бүртгэл үнээс болж удаашрах
     * ёсгүй: үнэгүй гутал тавиур дээрээ бүртгэгдэж, үнэ тавих хүртэл
     * касс хаана (`sellableReason`, `orders.ts`). Үнийг дараа нь баркодоор
     * «Үнэ оруулаагүй бараа» дээр тавина (`POST /items/:id/price`).
     */
    priceMnt: MoneyString.refine((v) => Number(v) > 0, 'Үнэ 0-ээс их байх ёстой').optional(),
});
export const OpeningSummaryQuery = z.object({ storeId: Uuid });
/**
 * Оросын бөөн аяллаас агуулах НЭГ гутал үүсгэх (`POST /purchases/:id/items`).
 * Нийлүүлэгч аяллаас; баркод сервер олгоно (огноо = өнөөдөр).
 *
 * `id` — клиентийн UUID v7, идемпотентлэг: давтан дарахад гутал хоёр
 * үүсэхгүй, шошго хоёр хэвлэгдэхгүй.
 */
export const TripItemInput = z.object({
    id: Uuid,
    storeId: Uuid,
    size: Size,
    /** Нийлүүлэгчид төлсөн дүн, аяллын валютаар (₽). 0 = мэдэгдэхгүй. */
    purchaseAmount: MoneyString.refine((v) => Number(v) >= 0, 'Дүн сөрөг байж болохгүй'),
    /**
     * Зарах үнэ — ЗААВАЛ БИШ (2026-09-24). Агуулах хайрцаг задлахдаа
     * үнийг үргэлж мэддэггүй; шошго наах ажил үүнээс болж зогсох ёсгүй.
     * Өгөөгүй бол гутал хүлээн авалтын (зарагдахгүй) байршилд үлдэж,
     * үнэ тавимагц лангуу/агуулахын тавиур руу өөрөө шилжинэ.
     */
    priceMnt: MoneyString.refine((v) => Number(v) > 0, 'Үнэ 0-ээс их байх ёстой').optional(),
    isCredit: z.boolean().default(false),
});
export const ItemIntakeInput = z.object({
    tempCode: z.string().min(8).max(20),
    storeId: Uuid.optional(),
    locationId: Uuid.optional(),
});
export const ItemBatchInput = z.object({
    /**
     * Оролдлогын шошго — аудит (`GET /system/audit?entityId=`), лог.
     * ⚠️ Давхардлаас ХАМГААЛАХГҮЙ — мөр бүрийн `id` хамгаална. Шинэ
     * operationId-тай дахин илгээсэн ч бараа давхардахгүй.
     */
    operationId: Uuid,
    items: z.array(ItemBatchLine).min(1).max(200),
    /** Заагаагүй бол ролийн анхдагч: COLLECTOR → SUPPLIER, WAREHOUSE/ADMIN → WAREHOUSE_INTAKE. */
    locationId: Uuid.nullable().optional(),
    /** ⚠️ ЗӨВХӨН WAREHOUSE / ADMIN. Серийн дугаар агуулахад олгогдоно (CLAUDE.md №6). */
    assignSerial: z.boolean().default(false),
});
/** Мөр тус бүрийн татгалзлын код. `SyncRejectOutput`-ын үгсийн сантай нийцтэй. */
export const ItemBatchRejectCode = z.enum([
    'PURCHASE_NOT_FOUND', 'PURCHASE_CLOSED', 'PURCHASE_MISMATCH',
    'MODEL_NOT_FOUND', 'MODEL_INACTIVE', 'SIZE_OUT_OF_RANGE',
    'SUPPLIER_NOT_FOUND', 'SUPPLIER_INACTIVE', 'CURRENCY_MISMATCH',
    'AMOUNT_NEGATIVE', 'COLLECTED_AT_FUTURE',
    // Утас өөрөө олгосон серийн дугаарын шалгалт
    'SERIAL_NOT_IN_BLOCK', 'SERIAL_TAKEN',
]);
/**
 * Мөр бүрийн үр дүн. `duplicate` нь АМЖИЛТ — гутал аль хэдийн бүртгэлтэй,
 * одоогийн серийн дугаар нь хариуд ирнэ (эхний хариу алдагдсан клиент
 * давталтаараа серийгээ олж авна).
 */
export const ItemBatchResult = z.object({
    id: Uuid,
    result: z.enum(['created', 'duplicate', 'rejected']),
    code: ItemBatchRejectCode.optional(),
    message: z.string().optional(),
    field: z.string().optional(),
    serial: Serial.nullable().optional(),
    publicToken: z.string().nullable().optional(),
    status: ItemStatus.optional(),
    locationId: Uuid.nullable().optional(),
    /**
     * ⚠️ Шинэ мөрөнд `null` — систем ₽-ийг ₮ болгож бодохгүй
     *    (шийдвэр 2026-09-16). Өмнө бүртгэгдсэн `duplicate` мөрөнд утгатай.
     */
    purchaseRate: RateString.nullable().optional(),
    /** Ханшийн мөрийн огноо ('YYYY-MM-DD') — аудитор яг тэр fx_rates мөрийг олно. */
    purchaseRateDate: z.string().nullable().optional(),
    goodsCostMnt: MoneyString.nullable().optional(),
});
export const ItemBatchOutput = z.object({
    created: z.number().int(),
    duplicate: z.number().int(),
    rejected: z.number().int(),
    results: z.array(ItemBatchResult),
});
export const AssignSerialInput = z.object({
    itemIds: z.array(Uuid).min(1).max(500),
});
/** Шошго хэвлэх — сервер ZPL текст буцаана, хэвлэлт нь төхөөрөмжөөс. */
export const PrintLabelsInput = z.object({
    itemIds: z.array(Uuid).min(1).max(200),
    widthMm: z.number().int().min(20).max(100).optional(),
    heightMm: z.number().int().min(15).max(100).optional(),
});
export const PrintLabelInput = PrintLabelsInput.omit({ itemIds: true });
export const MoveItemInput = z.object({
    itemIds: z.array(Uuid).min(1).max(500),
    toLocationId: Uuid,
    reason: MovementReason,
    note: z.string().max(500).optional(),
});
export const SetPriceInput = z.object({
    priceMnt: MoneyString,
    reason: z.string().max(200).optional(),
});
/**
 * Размер бөглөх — Оросод олноор бүртгэсэн гутал размергүй ирдэг
 * (дүрэм 6: цуглуулагч зөвхөн загвар, өнгө, үнийг мэднэ). Агуулах
 * уншуулах үедээ бөглөнө.
 */
export const SetSizeInput = z.object({
    size: Size,
});
/** Скан хийхэд — Luhn-ыг domain шалгах тул энд зөвхөн түүхий текст. */
export const SerialParam = z.object({
    serial: z.string().min(1).max(24),
});
/** Үнийн шүүлтүүр — ₮ бүхэл тоо. */
const PriceQuery = z.coerce.number().int().min(0).max(1_000_000_000);
/**
 * Гутлын шүүлтүүр — `GET /items` ба `GET /items/facets` ХОЁУЛАА.
 *
 * ⚠️ Хоёр хаяг ИЖИЛ шүүлтүүр авна: размерын карт дээрх тоо, дарсны
 *    дараах жагсаалтын нийт тоо хоёр зөрөх ёсгүй.
 */
export const ItemOrigin = z.enum(['trip', 'opening']);
export const ItemFilterQuery = z.object({
    status: ItemStatus.optional(),
    /**
     * true → «Нөөцөд»: дахин зарагдаж болох төлөв (AVAILABLE, RETURNED),
     * байршлаас үл хамаарна — агуулахад үнээ хүлээж буй гутал ч орно.
     */
    inStock: BoolQuery.optional(),
    locationId: Uuid.optional(),
    storeId: Uuid.optional(),
    modelId: Uuid.optional(),
    supplierId: Uuid.optional(),
    size: z.coerce.number().int().min(20).max(60).optional(),
    /** true → зөвхөн размергүй — агуулах бөглөх ёстой */
    unsized: BoolQuery.optional(),
    /** Размерын муж — прототипийн 36-39 / 40-42 / 43-46 шүүлтүүр */
    sizeMin: z.coerce.number().int().min(20).max(60).optional(),
    sizeMax: z.coerce.number().int().min(20).max(60).optional(),
    /** Загварын хүйсээр (products → items) */
    gender: Gender.optional(),
    material: Material.optional(),
    /** Серийн дугаар / загварын нэр / brand дээр хэсэгчилсэн хайлт */
    q: z.string().max(100).optional(),
    /** true → зөвхөн зээлээр авсан (нийлүүлэгчид өртэй) */
    isCredit: BoolQuery.optional(),
    /** true → зөвхөн шошгогүй (серийн олгогдоогүй) */
    unlabeled: BoolQuery.optional(),
    /** true → зөвхөн үнэ тавиагүй (0₮) — агуулахад үнээ хүлээж буй гутал */
    unpriced: BoolQuery.optional(),
    /** Зарах үнийн муж, хил орно */
    priceMin: PriceQuery.optional(),
    priceMax: PriceQuery.optional(),
    /**
     * Хаанаас орсон: `trip` — Оросын аялал, `opening` — POS-оор бүртгэсэн
     * «Эхний үлдэгдэл». Хоёр тусдаа бүртгэл (2026-09-29).
     */
    origin: ItemOrigin.optional(),
});
/** Жагсаалтын эрэмбэ. Анхдагч — сүүлд бүртгэгдсэн нь эхэнд. */
export const ItemSort = z.enum(['newest', 'oldest', 'price_asc', 'price_desc']);
export const ItemListQuery = Pagination.extend({
    ...ItemFilterQuery.shape,
    sort: ItemSort.default('newest'),
});
/**
 * `GET /items/facets` — «Бүх гутал»-ын размерын карт, шүүлтүүрийн тоо.
 *
 * Тоо бүр ӨӨРИЙН хэмжээсийг хасч бодогдоно: размерын карт размерын
 * шүүлтүүрийг, нэгжийн товч нэгжийнхийг тооцохгүй. Эс тэгвээс 38-ыг
 * сонгомогц бусад размер 0 болж, нэг товчоор шилжих боломжгүй болно.
 */
export const ItemFacetsOutput = z.object({
    /** Размер өсөхөөр, размергүй нь төгсгөлд (`sizeFacets`) */
    sizes: z.array(z.object({
        size: z.number().int().nullable(),
        count: z.number().int(),
        warehouse: z.number().int(),
        retail: z.number().int(),
    })),
    /** `inStock` — «Нөөцөд»-ийн нийлбэрт орох төлөв */
    statuses: z.array(z.object({ status: ItemStatus, count: z.number().int(), inStock: z.boolean() })),
    /** `null` — нэгжгүй байршил: замд, худалдан авагчид, актлагдсан */
    stores: z.array(z.object({ storeId: Uuid.nullable(), count: z.number().int() })),
    locations: z.array(z.object({ locationId: Uuid.nullable(), count: z.number().int() })),
    suppliers: z.array(z.object({ supplierId: Uuid, count: z.number().int() })),
    /** Одоогийн шүүлтүүр дээр «Шошгогүй» / «Үнэгүй» нэмбэл хэд болох */
    unlabeled: z.number().int(),
    unpriced: z.number().int(),
    /** Гарлаар — `origin` шүүлтүүрийг хасч бодсон */
    origins: z.object({ trip: z.number().int(), opening: z.number().int() }),
});
/** Cursor-based хуудаслалтын нийтлэг хариу. */
export const PageMeta = z.object({
    nextCursor: z.string().nullable(),
    count: z.number().int(),
});
// ============================================================
//  Зураг — API.md §5
//
//  Файл нь СЕРВЕРЭЭР ДАМЖИХГҮЙ. Клиент presigned URL авч объект
//  хадгалалт (dev: MinIO, prod: Vultr) руу шууд тавиад confirm дуудна.
// ============================================================
/** Баталгааны нотолгоо тул зөвхөн алдагдалгүй/өргөн дэмжигдсэн формат. */
export const PhotoContentType = z.enum(['image/jpeg', 'image/png', 'image/webp']);
/** 25 MB — орчин үеийн утасны бүтэн чанартай зураг багтана. */
export const PHOTO_MAX_BYTES = 25 * 1024 * 1024;
export const PhotoPresignInput = z.object({
    contentType: PhotoContentType,
    /**
     * ЗӨВЛӨМЖ шинжтэй — Zod-ын max(25MB) л шалгана, объект хадгалалт
     * PUT дээр хэмжээ ХЯЗГААРЛАХГҮЙ. Presigned PUT-д ContentLength
     * гарын үсэглэвэл байт зөрөхөд Оросоос 403 (логгүй) авна — зориуд
     * хийгээгүй, docs/BACKLOG.md-д хойшлуулсан.
     */
    bytes: z.number().int().positive().max(PHOTO_MAX_BYTES).optional(),
});
export const PhotoPresignOutput = z.object({
    /** Клиент дээр биш, СЕРВЕРТ үүснэ — түлхүүр үүнээс бүрдэнэ. */
    photoId: Uuid,
    key: z.string(),
    uploadUrl: z.string().url(),
    expiresIn: z.number().int(),
});
/**
 * Upload дууссаны дараа. ЭНЭ мөчид л `item_photos` мөр үүснэ.
 *
 * `contentType` нь presign дээр илгээсэнтэй ижил байх ёстой — сервер
 * түүгээр түлхүүрийг ДАХИН байгуулна. Клиент түлхүүрээ өөрөө
 * дамжуулдаггүй тул дурын зам сонгох боломжгүй.
 */
export const PhotoConfirmInput = z.object({
    photoId: Uuid,
    contentType: PhotoContentType,
    role: PhotoRole.default('DETAIL'),
    sortOrder: z.number().int().min(0).max(999).default(0),
    capturedAt: IsoDate.optional(),
});
export const PhotoUpdateInput = z
    .object({
    role: PhotoRole.optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
})
    .refine((v) => v.role !== undefined || v.sortOrder !== undefined, {
    message: 'role эсвэл sortOrder-ын аль нэгийг заана',
});
export const PhotoListQuery = z.object({ role: PhotoRole.optional() });
export const PhotoDuplicatesQuery = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
// ============================================================
//  Худалдан авалт — landed cost
// ============================================================
export const PurchaseCostInput = z.object({
    transportMnt: MoneyString.default('0'),
    customsMnt: MoneyString.default('0'),
    otherMnt: MoneyString.default('0'),
});
export const AllocateInput = z.object({
    method: CostAllocation,
});
export const PurchaseCreateInput = z.object({
    /**
     * Клиент дээр үүснэ (UUID v7) — `purchases` PK. Давхардлын түлхүүр нь
     * ЭНЭ: офлайн апп нэг хүсэлтийг олон удаа давтдаг тул ижил `id`-тай
     * аялал хоёр дахь удаа шинийг ҮҮСГЭХГҮЙ. `orders`-ийн зан чанартай
     * ижил, Prisma схем ч анхнаасаа ингэж тайлбарласан.
     */
    id: Uuid,
    supplierId: Uuid,
    purchasedAt: IsoDate,
    currency: z.string().length(3).default('RUB'),
    goodsAmount: MoneyString.default('0'),
    allocation: CostAllocation.default('BY_VALUE'),
    notes: z.string().max(2000).optional(),
});
/** Зөвхөн DRAFT / SYNCED үед. Нэгжийн өртөг тооцогдсоны дараа хаагдана. */
export const PurchaseUpdateInput = z.object({
    supplierId: Uuid.optional(),
    purchasedAt: IsoDate.optional(),
    goodsAmount: MoneyString.optional(),
    allocation: CostAllocation.optional(),
    notes: z.string().max(2000).optional(),
});
export const PurchaseListQuery = Pagination.extend({
    status: PurchaseStatus.optional(),
    supplierId: Uuid.optional(),
    /** Аяллыг ЯВСАН ажилтан (`purchases.collector_id`). */
    collectorId: Uuid.optional(),
    /** Аялсан огноогоор (`purchased_at`) — бүтэн агшин, УБ-ийн заагийг клиент өгнө. */
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
/** Хуваарилалтыг бичихээс өмнө туршиж үзэх. */
export const CostPreviewQuery = z.object({
    method: CostAllocation.optional(),
});
/**
 * Аялал дуусгах — цуглуулагч нийлүүлэгчид өгсөн НИЙЛБЭРЭЭ зарлана.
 *
 * ⚠️ ₽ ба ₮ нь ТУСДАА бодит төлбөр, нэг нь нөгөөгийн хөрвүүлэг БИШ.
 *    Гутлын мөрүүд рублиэр тул зөрүү зөвхөн ₽ талд гарна.
 *
 * ⚠️ Зээл нь нийтийн ДОТОР — «нийт 120000₽, үүнээс 40000₽ зээл» гэсэн
 *    утгатай. Тиймээс зээл нийтээс их байж болохгүй.
 */
export const PurchaseCloseInput = z
    .object({
    declaredTotalRub: MoneyString.default('0'),
    declaredCreditRub: MoneyString.default('0'),
    declaredTotalMnt: MoneyString.default('0'),
    declaredCreditMnt: MoneyString.default('0'),
    /** Цуглуулагчийн зарласан ширхэг (Оросын бөөн бүртгэл). */
    declaredCount: z.number().int().min(0).max(100_000).default(0),
    /**
     * Зээлээ хэзээ төлөхөөр тохирсон. Зээл байхгүй бол утгагүй.
     *
     * ⚠️ `FxCreateInput.date`-тай ижил конвенц: бүтэн ISO datetime
     *    илгээгээд DB нь `@db.Date` болгож хадгална.
     */
    creditDueDate: IsoDate.optional(),
})
    .superRefine((v, ctx) => {
    // MoneyString нь 2 оронтой тул Number хангалттай нарийвчлалтай —
    // энэ нь ЗӨВХӨН шалгалт, тооцоо нь сервер талд Decimal-аар явна.
    if (Number(v.declaredCreditRub) > Number(v.declaredTotalRub)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['declaredCreditRub'],
            message: 'Зээлийн дүн нийт дүнгээс их байж болохгүй',
        });
    }
    if (Number(v.declaredCreditMnt) > Number(v.declaredTotalMnt)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['declaredCreditMnt'],
            message: 'Зээлийн дүн нийт дүнгээс их байж болохгүй',
        });
    }
    // Зээлтэй бол хугацаа ЗААВАЛ — эс тэгвээс өр хэзээ болохыг хэн ч
    // мэдэхгүй, вэб анхааруулах боломжгүй болно.
    const hasCredit = Number(v.declaredCreditRub) > 0 || Number(v.declaredCreditMnt) > 0;
    if (hasCredit && v.creditDueDate === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['creditDueDate'],
            message: 'Зээлтэй бол төлөх хугацааг заана уу',
        });
    }
});
// ------------------------------------------------------------
//  Хүлээн авалтын гэмтэл — `POST /purchases/:id/defects`
//
//  Хайрцаг задлахад илэрсэн гэмтлийг агуулах бүртгэнэ. Цуглуулагчийн
//  сарын оноог бууруулдаг (`leaderboard-sources.ts`) тул алдаатай
//  бүртгэлийг УСТГАХГҮЙ, `voidedAt`-аар цуцална — түүх үлдэнэ.
// ------------------------------------------------------------
export const DefectSeverity = z.enum(['MINOR', 'MAJOR']);
export const ReceivingDefectInput = z.object({
    /** Клиентийн UUID v7 — давтан дарахад гэмтэл хоёр бүртгэгдэхгүй. */
    id: Uuid,
    /** Тодорхой гутал дээр бол — аль хэдийн бүртгэгдсэн байх ёстой. */
    itemId: Uuid.nullish(),
    severity: DefectSeverity,
    /** `Lookup(DEFECT_TYPE)` — жагсаалт нь захиалагчийн мэдэлд. */
    typeLookupId: Uuid,
    note: z.string().max(500).optional(),
});
/** `/purchases/:id/defects/:defectId/void` */
export const DefectParams = z.object({ id: Uuid, defectId: Uuid });
// ------------------------------------------------------------
//  Нийлүүлэгчид төлсөн төлбөр — `POST /purchases/:id/payments`
//
//  ⚠️ Зөвхөн админ. Мөнгөний бүртгэл тул УСТГАХГҮЙ, цуцална.
// ------------------------------------------------------------
export const SupplierPaymentInput = z.object({
    /** Клиентийн UUID — давтан дарахад төлбөр хоёр бүртгэгдэхгүй. */
    id: Uuid,
    /** Аяллын валютаар (₽). 0-ээс их байх ёстой. */
    amount: MoneyString.refine((v) => Number(v) > 0, 'Дүн 0-ээс их байх ёстой'),
    /**
     * Бодитоор зарцуулсан ₮ (2026-10-15) — ханшийн тэгшитгэлд. Заавал биш:
     * хуучин клиент илгээхгүй; оруулаагүй төлбөр тэгшитгэлд «₮ бүртгээгүй» гарна.
     */
    amountMnt: MoneyString.refine((v) => Number(v) > 0, 'Дүн 0-ээс их байх ёстой').optional(),
    /** Хэзээ төлсөн — өгөөгүй бол одоо. Ирээдүйн огноо болохгүй. */
    paidAt: IsoDate.optional(),
    note: z.string().max(500).optional(),
});
/** `/purchases/:id/payments/:paymentId/void` */
export const PaymentParams = z.object({ id: Uuid, paymentId: Uuid });
/** Хүлээн авах байршил — заагаагүй бол агуулахын хүлээн авалт. */
export const PurchaseReceiveInput = z.object({
    locationId: Uuid.optional(),
    note: z.string().max(500).optional(),
});
// ============================================================
//  Борлуулалт
// ============================================================
export const OrderLineInput = z.object({
    itemId: Uuid,
    discountMnt: MoneyString.default('0'),
    /**
     * Баталгааны хугацаа — өгөөгүй бол загварын анхдагч.
     *
     * ⚠️ Хаалттай жагсаалттай (`@boots/domain` → `WARRANTY_PRESETS`),
     *    гэхдээ шалгалт нь ЭНД биш handler-т: `packages/schema` нь
     *    `@boots/domain`-аас хамаардаггүй (мөчлөг). Жагсаалтыг хоёр
     *    газар давтахаас нэг газар шалгасан нь дээр.
     */
    warrantyDays: z.number().int().min(1).max(3650).optional(),
});
/**
 * СОЛИЛЦОО — `POST /orders`-ийн нэмэлт блок.
 *
 * ЯАГААД ТУСДАА ENDPOINT БИШ ВЭ
 *  Захиалга ӨӨРЧЛӨГДӨШГҮЙ (CLAUDE.md №2, DB trigger хамгаална) тул
 *  солилцоо гэдэг нь «хуучин мөрийг солих» биш, ХОЁР баримт:
 *    буцаалт (хуучин гутал) + ШИНЭ ЗАХИАЛГА (солих гутал)
 *  Хоёулаа нэг транзакцад байх ёстой — дунд нь тасарвал гутал
 *  буцаагдчихаад орлого бүртгэгдэхгүй үлдэнэ.
 *
 *  Тиймээс шинэ борлуулалтын урсгалыг ДАВХАРДУУЛАЛГҮЙ, байгаа
 *  `POST /orders` транзакцад буцаалтыг НЭМЖ хийнэ. Ингэснээр
 *  snapshot, баталгаа, урамшуулал, түгжээ бүгд хэвээр ажиллана.
 *
 * ЗӨРҮҮ (2026-10-03, `exchangeSettlement` — `@boots/domain`):
 *  · шинэ гутал ҮНЭТЭЙ — худалдан авагч зөрүүг `payment`/`payments`-аар
 *    нэмж төлнө; хуучны үнэ шинэ баримтад `EXCHANGE` төлбөр болно
 *  · шинэ гутал ХЯМД — зөрүүг `refundMethod`-оор буцааж олгоно
 *  `EXCHANGE` төлбөрийн мөрийг СЕРВЕР бичнэ, клиент илгээхгүй.
 */
export const OrderExchangeInput = z.object({
    /** Буцаагдаж буй хуучин захиалгын мөр */
    orderLineId: Uuid,
    reasonLookupId: Uuid,
    disposition: ReturnDisposition.default('RESTOCKED'),
    /** Буцаасан гутлыг хаашаа тавих. Заагаагүй бол disposition-оос. */
    locationId: Uuid.optional(),
    notes: z.string().max(1000).optional(),
    /** Шинэ гутал хямд бол зөрүүг ямар хэлбэрээр буцаах — тэр үед ЗААВАЛ */
    refundMethod: RefundMethod.optional(),
});
/**
 * Захиалга батлах — НЭГ дуудлагаар.
 *
 * ⚠️ API.md §8-д `POST /orders/draft` + мөр нэмэх endpoint-ууд байсан.
 *    Схем түүнийг зөвшөөрөхгүй: `order_lines` нь INSERT-ээс хойш бүрэн
 *    өөрчлөгдөшгүй (`trg_lines_immutable`), `OrderStatus`-д DRAFT байхгүй,
 *    сагсны хүснэгт ч алга. Тиймээс сагс нь клиент дээр, сервер рүү
 *    нэг л удаа бүтнээр ирнэ — 7 үйлдэл нэг транзакцад.
 *
 * `id` нь клиент дээр үүссэн UUID. Давтан илгээвэл шинэ захиалга
 * үүсэхгүй, өмнөх нь 200 + `meta.idempotent` гэж буцна — давхардлын
 * түлхүүр нь энэ `id`. `Idempotency-Key` header БАЙХГҮЙ.
 */
export const OrderConfirmInput = z.object({
    id: Uuid.optional(),
    storeId: Uuid.optional(),
    channel: SaleChannel.default('STORE'),
    deviceId: Uuid.nullable().optional(),
    lines: z.array(OrderLineInput).min(1).max(50),
    customerPhone: PhoneMn.nullable().optional(),
    customerName: z.string().max(200).optional(),
    /** Регистр — нэмэлт баримт. Нормчлол `@boots/domain`-д, тиймээс энд түүхий. */
    customerRegistryNo: z.string().max(20).nullable().optional(),
    marketingConsent: z.boolean().default(false),
    /**
     * Өгсөн урамшууллын НЭР («Цахилгаан бараа»). Сервер `lookups(PROMOTION)`-оос
     * нэрээр олно, байхгүй бол үүсгэнэ (нуусан бол дахин идэвхжүүлнэ) — ID биш
     * нэр тул кассчин анх удаа шивсэн ч нэг л талбар.
     */
    promotion: z.string().max(200).optional(),
    /**
     * Хураангуй хэлбэр. `payments` өгсөн үед сервер ҮҮНИЙГ ӨӨРӨӨ бодно
     * (нэг хэлбэр → тэр нь, хэд хэдэн → `MIXED`) — клиентийн утга
     * хөндөгдөнө.
     */
    payment: PaymentMethod,
    /**
     * Хэсэгчилсэн төлбөр — «500,000₮ бэлнээр, үлдсэнийг LendMN-ээр».
     *
     * ⚠️ Нийлбэр нь захиалгын НИЙТ дүнтэй яг таарах ёстой (сервер
     *    шалгана). Өгөөгүй бол `payment` хэлбэрээр бүтэн дүн гэж үзэж
     *    сервер НЭГ мөр үүсгэнэ — тайлан үргэлж `order_payments`-аас
     *    уншина.
     */
    payments: z
        .array(z.object({
        method: PaymentMethod,
        /** Зөвхөн `CREDIT_APP` үед — `lookups(PAYMENT_PROVIDER)`. */
        providerId: Uuid.optional(),
        amountMnt: MoneyString,
    }))
        .min(1)
        .max(5)
        .optional(),
    externalPosRef: z.string().max(100).optional(),
    discountMnt: MoneyString.default('0'),
    /**
     * ⚠️ ХУУЧИН клиентийг танихад л. Зээлийн аппын 7% 2026-09-28-нд
     *    ЦУЦЛАГДСАН — шинэ клиент энэ талбарыг илгээхгүй. `true` = 7%-ийг
     *    төлбөртөө нэмсэн хуучин POS/вэб → «аппаа шинэчил» 422.
     */
    creditFee: z.boolean().optional(),
    /**
     * Кассын нэмэгдэл — и-баримт +10%. Дүнг клиент ӨГӨХГҮЙ, зөвхөн
     * сонголтыг: сервер `surchargeFor`-оор бодоод `payments`-ийн нийлбэрийг
     * НЭМЭГДЭЛТЭЙ дүнтэй тулгана.
     *
     * ⚠️ `CREDIT_APP` төлбөр орсон бол ҮЛ ХАМААРНА — и-баримт заавал, зөвхөн
     *    аппын хэсэгт (`creditAppBase`, 2026-10-06).
     */
    vat: z.boolean().default(false),
    /**
     * Хувь зээл — төлбөрт `INSTALLMENT` орсон бол ЗААВАЛ. Утас, регистр нь
     * `customerPhone`, `customerRegistryNo`-оос (хоёулаа заавал болно).
     */
    installment: z.object({ termMonths: z.number().int().min(1).max(6) }).optional(),
    /** Өгвөл энэ захиалга нь СОЛИЛЦОО — хуучин мөр нэг зэрэг буцаагдана */
    exchange: OrderExchangeInput.optional(),
});
/**
 * `GET /orders/day-close` — POS-ын өдрийн хаалтын тайлан.
 * `date` — Улаанбаатарын өдөр; өгөөгүй бол өнөөдөр.
 */
export const DayCloseQuery = z.object({
    storeId: Uuid,
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Огноо YYYY-MM-DD' }).optional(),
});
/**
 * `GET /reports/calendar` — сарын нүд. `month` өгөөгүй бол энэ сар (УБ).
 * `GET /reports/calendar/day` — нэг өдрийн дэлгэрэнгүй.
 */
export const CalendarMonthQuery = z.object({
    month: z.string().regex(/^\d{4}-\d{2}$/, { message: 'Сар YYYY-MM' }).optional(),
});
export const CalendarDayQuery = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Огноо YYYY-MM-DD' }),
});
export const OrderListQuery = Pagination.extend({
    storeId: Uuid.optional(),
    soldById: Uuid.optional(),
    customerId: Uuid.optional(),
    status: OrderStatus.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    /**
     * Захиалгын код (`S-2026-000123`) эсвэл худалдан авагчийн утас.
     * Утсыг нормчилж хайна — «99112233», «+97699112233» хоёул олдоно.
     */
    q: z.string().min(2).max(40).optional(),
    /** Нийт дүнгийн хязгаар — «их дүнтэй захиалгууд». */
    minTotal: MoneyString.optional(),
    maxTotal: MoneyString.optional(),
});
/** Хүчингүй болгох — шалтгаан ЗААВАЛ. Устгал гэж байхгүй. */
export const OrderVoidInput = z.object({
    reason: z.string().min(3).max(500),
});
// ---- Хувь зээл — `/installments` ----
/** `status`: нээлттэй · хугацаа хэтэрсэн · хаагдсан · бүгд. `q` — утас эсвэл регистр. */
export const InstallmentListQuery = z.object({
    status: z.enum(['open', 'overdue', 'closed', 'all']).default('open'),
    storeId: Uuid.optional(),
    q: z.string().min(2).max(40).optional(),
});
/** Сарын төлөлт — эхний 10%-ийн адил зөвхөн CASH · CARD · QPAY · TRANSFER. */
export const InstallmentPaymentInput = z.object({
    method: z.enum(['CASH', 'CARD', 'QPAY', 'TRANSFER']),
    amountMnt: MoneyString,
    note: z.string().max(500).optional(),
});
export const InstallmentPaymentVoidInput = z.object({
    reason: z.string().min(3).max(500),
});
export const InstallmentPaymentParams = z.object({ id: Uuid, paymentId: Uuid });
export const ReturnListQuery = Pagination.extend({
    storeId: Uuid.optional(),
    disposition: ReturnDisposition.optional(),
    /** Бүтэн буцаалт эсвэл солилцоо */
    type: ReturnType.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const ClaimListQuery = Pagination.extend({
    resolution: ClaimResolution.optional(),
    supplierId: Uuid.optional(),
});
export const WarrantyExpiringQuery = z.object({
    days: z.coerce.number().int().min(1).max(3650).default(30),
});
export const SalesReportQuery = z.object({
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    storeId: Uuid.optional(),
    /**
     * `priceBand` — «хэдэн төгрөгийн гутал хамгийн их зарагддаг вэ»
     * `weekday`  — «долоо хоногийн аль өдөр их зардаг вэ»
     */
    groupBy: z
        .enum(['day', 'month', 'store', 'model', 'seller', 'priceBand', 'weekday'])
        .default('day'),
});
/** `/reports/sales/export` — тайлантай ижил шүүлт, бүлэглэлтгүй (гутал бүр нэг мөр). */
export const SalesExportQuery = SalesReportQuery.omit({ groupBy: true });
/**
 * `/report-files/:kind` — тайлангийн Excel файл (AI туслахаар, 2026-10-06).
 * Түлхүүрийн жагсаалт `@boots/domain` → `REPORT_KINDS`; энд зөвхөн хэлбэр,
 * каталогт байгаа эсэхийг route шалгана.
 */
export const ReportFileParam = z.object({ kind: z.string().regex(/^[a-z_]{2,40}$/) });
/**
 * `POST /assistant/ask` — AI туслахад асуулт. Түүхийг клиент хадгалж
 * сүүлийн хэдэн мессежийг л илгээнэ (сервер харилцан яриа хадгалахгүй).
 */
export const AssistantAskInput = z.object({
    messages: z
        .array(z.object({ role: z.enum(['user', 'assistant']), content: z.string().min(1).max(2000) }))
        .min(1)
        .max(12),
    /** Ажилтан аль хуудсан дээр байгаа — '/sales' */
    page: z.string().max(200).optional(),
});
/** Утсаар хайх — нормчлолыг domain хийнэ, энд түүхий текст. */
export const PhoneParam = z.object({ phone: z.string().min(6).max(20) });
export const PublicTokenParam = z.object({
    token: z.string().min(10).max(24),
});
export const CustomerUpsertInput = z.object({
    phone: PhoneMn,
    name: z.string().max(200).nullable().optional(),
    email: z.string().email().max(200).nullable().optional(),
    /** `УБ12345678`. Нормчлол `@boots/domain` → `normalizeRegistryNo`. */
    registryNo: z.string().max(20).nullable().optional(),
    marketingConsent: z.boolean().optional(),
});
/**
 * Буцаалтын хайлт — ГУРВАН зам, аль нэг нь заавал.
 *
 * Худалдан авагч шошготойгоо ирвэл `serial`, баримттай ирвэл `code`,
 * хоёулангүй ирвэл `phone`. Гуравдугаар зам нь хамгийн түгээмэл —
 * шошго нь гутлын дотор талд, худалдан авагч сугалж хаядаг.
 */
export const ReturnLookupQuery = z
    .object({
    serial: z.string().max(24).optional(),
    /** Захиалгын код — `S-2026-004521` */
    code: z.string().max(24).optional(),
    phone: z.string().max(20).optional(),
})
    .refine((v) => Boolean(v.serial ?? v.code ?? v.phone), { message: 'Серийн дугаар, баримтын код, утасны аль нэгийг өгнө' });
/**
 * Төлбөрийг бүтнээр буцаах — НЭГ гутал (захиалагчийн шийдвэр 2026-10-03).
 * Дүн нь ҮРГЭЛЖ мөрийн бүтэн дүн — сервер өөрөө тавина, клиент өгөхгүй
 * (өмнө нь дурын дүн оруулах талбар байв). Солилцоо нь `POST /orders`-оор.
 */
export const ReturnInput = z.object({
    reasonLookupId: Uuid,
    disposition: ReturnDisposition,
    /** Мөнгийг ямар хэлбэрээр буцааж өгсөн */
    refundMethod: RefundMethod,
    /**
     * Буцаасан гутлыг ХААШАА тавих вэ.
     *
     * Заагаагүй бол `disposition`-оос автоматаар сонгоно (RESTOCKED →
     * тухайн салбарын худалдааны танхим, DEFECTIVE → хорио цээр…).
     * Заасан бол ЯГ тэр байршилд очно — ажилтан «энэ хосыг агуулах руу
     * буцаая» гэж шийдэж чадна.
     *
     * ⚠️ Байршил нь `disposition`-той зөрчилдөж БОЛНО (жишээ нь
     *    актлагдсан гутлыг өөр агуулахад хадгалах) — тэр нь ажилтны
     *    шийдвэр. Гутлын ТӨЛӨВ нь `disposition`-оос л гарна, байршлаас
     *    биш (CLAUDE.md №9).
     */
    locationId: Uuid.optional(),
    notes: z.string().max(1000).optional(),
});
// ============================================================
//  Баталгаа
// ============================================================
export const ClaimCreateInput = z.object({
    orderLineId: Uuid,
    defectLookupId: Uuid,
    defectDescription: z.string().max(2000).optional(),
});
export const ClaimResolveInput = z.object({
    resolution: ClaimResolution.exclude(['PENDING']),
    newItemId: Uuid.nullable().optional(),
    rejectReasonId: Uuid.nullable().optional(),
    repairCostMnt: MoneyString.nullable().optional(),
    refundMnt: MoneyString.nullable().optional(),
});
// ============================================================
//  Шилжүүлэг, тооллого
// ============================================================
export const TransferCreateInput = z.object({
    fromStoreId: Uuid,
    toStoreId: Uuid,
    notes: z.string().max(500).optional(),
});
export const ScanInput = z.object({
    serial: Serial,
});
export const ScanBatchInput = z.object({
    serials: z.array(z.string()).min(1).max(1000),
    scannedAt: IsoDate.optional(),
});
export const TransferListQuery = Pagination.extend({
    status: TransferStatus.optional(),
    /** Илгээсэн ЭСВЭЛ хүлээн авах салбар */
    storeId: Uuid.optional(),
});
export const ItemIdParam = z.object({ id: Uuid, itemId: Uuid });
/**
 * `GET /transfers/inbound` — салбарт ирж буй (`incoming` = IN_TRANSIT)
 * эсвэл хүлээн авсан (`done` = RECEIVED/PARTIAL). Вэбийн «Хүлээн авалт».
 */
export const TransferInboundQuery = Pagination.extend({
    state: z.enum(['incoming', 'done']).default('incoming'),
});
/** Хүлээн авахад дутуу гарвал PARTIAL болно — тайлбар бичих боломж. */
export const TransferReceiveInput = z
    .object({
    note: z.string().max(500).optional(),
    /**
     * Вэбийн «Хүлээн авалт» (2026-10-03): гутал бүрийг уншуулахгүй —
     * шилжүүлгийн БҮХ гутал ирсэнд тооцогдож, `missingItemIds`-д заасан
     * нь л замд үлдэнэ. Өгөөгүй бол гар утасны зан: `scan-in` хийсэн нь л.
     */
    all: z.boolean().optional(),
    missingItemIds: z.array(Uuid).max(1000).optional(),
})
    .refine((b) => b.all || !b.missingItemIds?.length, {
    message: 'Дутуу гутлыг зөвхөн «бүгдийг хүлээн авах» үед заана',
    path: ['missingItemIds'],
});
export const StockCountCreateInput = z.object({
    storeId: Uuid,
    notes: z.string().max(500).optional(),
});
export const StockCountListQuery = Pagination.extend({
    storeId: Uuid.optional(),
    status: StockCountStatus.optional(),
});
export const StockCountCloseInput = z.object({
    notes: z.string().max(500).optional(),
});
// ============================================================
//  Актлалт
// ============================================================
/**
 * Актлалт. `approvedById` нь захиалагчийн шаардлага — хулгайг
 * "гэмтсэн" гэж нуухаас сэргийлнэ. Актлагч ба зөвшөөрөгч
 * өөр хүн байх ёстой.
 */
export const WriteOffInput = z.object({
    reasonLookupId: Uuid,
    approvedById: Uuid,
    note: z.string().max(1000).optional(),
});
export const WriteOffListQuery = Pagination.extend({
    storeId: Uuid.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
/** Загвар / размераар ЭСВЭЛ шууд жагсаалтаар бөөнөөр үнэ тавих. */
export const BulkPriceInput = z
    .object({
    itemIds: z.array(Uuid).min(1).max(500).optional(),
    modelId: Uuid.optional(),
    size: Size.optional(),
    priceMnt: MoneyString,
    reason: z.string().max(200).optional(),
})
    .refine((v) => !!v.itemIds || !!v.modelId, {
    message: 'itemIds эсвэл modelId-ийн аль нэгийг заавал өгнө',
});
// ============================================================
//  Лавлах — бичих (CRUD). API.md §3
// ============================================================
export const UserCreateInput = z.object({
    username: z.string().min(3).max(50).regex(/^[a-z0-9_.-]+$/, {
        message: 'Нэвтрэх нэр зөвхөн латин жижиг үсэг, тоо, . _ - агуулна',
    }),
    password: z.string().min(8).max(200),
    fullName: z.string().min(2).max(150),
    phone: PhoneMn.nullable().optional(),
    role: UserRole,
    storeId: Uuid.nullable().optional(),
});
/** Нууц үг энд ОРОХГҮЙ — `reset-password` тусдаа endpoint. */
export const UserUpdateInput = z.object({
    fullName: z.string().min(2).max(150).optional(),
    phone: PhoneMn.nullable().optional(),
    role: UserRole.optional(),
    storeId: Uuid.nullable().optional(),
    isActive: z.boolean().optional(),
});
export const ResetPasswordInput = z.object({
    newPassword: z.string().min(8).max(200),
});
export const UserListQuery = z.object({
    role: UserRole.optional(),
    storeId: Uuid.optional(),
    active: BoolQuery.optional(),
});
/**
 * Газрын зураг дээрх байрлал.
 *
 * ⚠️ ХОЁУЛАА эсвэл хоёулаа NULL. Зөвхөн нэгийг нь бөглөвөл зурагдаж
 *    чадахгүй ч «байрлалтай» мэт харагдана. DB-д ч CHECK-тэй
 *    (`infra/sql/002`) — энэ нь зөвхөн эрт татгалзах давхарга.
 *
 * ⚠️ Мөнгө биш тул `number` (CLAUDE.md №3 нь МӨНГӨНД хамаарна).
 *    Координатыг хэзээ ч нэмж хасдаггүй — алдаа хуримтлагдахгүй.
 */
const Coords = {
    lat: z.number().min(-90).max(90).nullable().optional(),
    lng: z.number().min(-180).max(180).nullable().optional(),
};
const coordsPaired = (v) => (v.lat === null || v.lat === undefined) === (v.lng === null || v.lng === undefined);
const COORDS_MESSAGE = { message: 'Өргөрөг, уртрагийг хамт өгнө', path: ['lat'] };
export const StoreCreateInput = z
    .object({
    code: z.string().min(1).max(20).regex(/^[A-Z0-9-]+$/, {
        message: 'Салбарын код латин том үсэг, тоо, зураасаас бүрдэнэ',
    }),
    name: z.string().min(1).max(150),
    type: StoreType,
    address: z.string().max(300).nullable().optional(),
    phone: PhoneMn.nullable().optional(),
    /** Хариуцагч ажилтан — `users.storeId`-ЭЭС ӨӨР (тэр нь ажлын байр) */
    managerId: Uuid.nullable().optional(),
    ...Coords,
})
    .refine(coordsPaired, COORDS_MESSAGE);
export const StoreUpdateInput = z
    .object({
    name: z.string().min(1).max(150).optional(),
    address: z.string().max(300).nullable().optional(),
    phone: PhoneMn.nullable().optional(),
    managerId: Uuid.nullable().optional(),
    isActive: z.boolean().optional(),
    ...Coords,
})
    .refine(coordsPaired, COORDS_MESSAGE);
export const LocationCreateInput = z.object({
    code: z.string().min(1).max(30).regex(/^[A-Z0-9-]+$/),
    name: z.string().min(1).max(150),
    type: LocationType,
    storeId: Uuid.nullable().optional(),
    isSellable: z.boolean().default(false),
    isCountable: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
});
export const LocationUpdateInput = z.object({
    name: z.string().min(1).max(150).optional(),
    isSellable: z.boolean().optional(),
    isCountable: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const SupplierCreateInput = z.object({
    code: z.string().min(1).max(30).regex(/^[A-Z0-9-]+$/),
    /** Дэлгүүрийн шошгоны эхний 2 орон. `null` = угтварыг арилгана. */
    barcodePrefix: z
        .string()
        .regex(/^\d{2}$/, 'Баркодын угтвар яг 2 цифр байна')
        .nullable()
        .optional(),
    name: z.string().min(1).max(150),
    type: SupplierType,
    region: z.string().max(100).nullable().optional(),
    city: z.string().max(100).nullable().optional(),
    contact: z.string().max(150).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
});
export const SupplierUpdateInput = SupplierCreateInput.partial()
    .omit({ code: true })
    .extend({
    isActive: z.boolean().optional(),
    /**
     * Түүхтэй нийлүүлэгчийг ИДЭВХГҮЙ болгоход заавал. Утга нь
     * `SUPPLIER_ARCHIVE_PHRASE`-тэй тохирох ёстой.
     *
     * ⚠️ Шалгалт нь СЕРВЕР дээр. UI дээр л шалгавал `curl`-ээр
     *    тойрч гарна — тэгвэл баталгаа нь чимэглэл болно.
     */
    confirm: z.string().max(64).optional(),
});
/**
 * Түүхтэй нийлүүлэгчийг архивлахад бичүүлэх хэллэг.
 *
 * ⚠️ Санамсаргүй дарахаас сэргийлэх зорилготой — нууцлал БИШ.
 *    Тиймээс UI дээр ил харагдана, хэрэглэгч хуулж бичнэ.
 */
export const SUPPLIER_ARCHIVE_PHRASE = 'tsaatan.biz';
/**
 * ⚠️ АНХДАГЧ нь идэвхтэй нийлүүлэгч л буцаана. Үүнийг өөрчилж
 *    БОЛОХГҮЙ: `apps/mobile/src/ref/cache.ts` энэ жагсаалтыг офлайн
 *    лавлах болгон Орос руу авч явдаг, `purchasing.tsx` шинэ аялалын
 *    сонголтод хэрэглэдэг. Архивласан нийлүүлэгч тэнд гарвал
 *    архивлах гэсэн утга нь алдагдана.
 *
 * `?includeArchived=true` — ЗӨВХӨН Лавлах хуудасны удирдах дэлгэц.
 */
export const SupplierListQuery = z.object({
    includeArchived: z.literal('true').optional(),
});
export const LookupCreateInput = z.object({
    category: LookupCategory,
    code: z.string().min(1).max(50).regex(/^[A-Z0-9_]+$/),
    labelMn: z.string().min(1).max(150),
    labelRu: z.string().max(150).nullable().optional(),
    sortOrder: z.number().int().min(0).default(0),
});
export const LookupUpdateInput = z.object({
    labelMn: z.string().min(1).max(150).optional(),
    labelRu: z.string().max(150).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
});
export const LookupReorderInput = z.object({
    order: z.array(z.object({ id: Uuid, sortOrder: z.number().int().min(0) })).min(1).max(200),
});
export const ModelCreateInput = z.object({
    code: z.string().min(1).max(50).regex(/^[A-Z0-9-]+$/),
    brand: z.string().min(1).max(100),
    name: z.string().min(1).max(150),
    colorwayId: Uuid.nullable().optional(),
    gender: Gender,
    material: Material,
    isUnique: z.boolean().default(true),
    sizeMin: Size.nullable().optional(),
    sizeMax: Size.nullable().optional(),
    warrantyDays: z.number().int().min(0).max(3650).default(365),
    warrantyTermsId: Uuid,
});
export const ModelUpdateInput = ModelCreateInput.partial()
    .omit({ code: true })
    .extend({ isActive: z.boolean().optional() });
/** Төхөөрөмжийн код — хүн уншихад зориулсан нэр (`DEV-RU-01`), нууц БИШ. */
export const DeviceCode = z.string().min(1).max(30).regex(/^[A-Z0-9-]+$/);
export const DeviceCreateInput = z.object({
    code: DeviceCode,
    type: DeviceType,
    label: z.string().min(1).max(150),
    serialNumber: z.string().max(100).nullable().optional(),
    storeId: Uuid.nullable().optional(),
    assignedToId: Uuid.nullable().optional(),
});
export const DeviceUpdateInput = z.object({
    label: z.string().min(1).max(150).optional(),
    storeId: Uuid.nullable().optional(),
    assignedToId: Uuid.nullable().optional(),
});
/** Хаах шалтгаан заавал — алдагдсан төхөөрөмж бол мөрдөх ул мөр хэрэгтэй. */
export const DeviceBlockInput = z.object({
    reason: z.string().min(3).max(300),
});
/**
 * Серийн дугаарын муж гуйх — УТАС ӨӨРӨӨ, онлайн байхдаа.
 *
 * ⚠️ Админ гараар олгодог байвал аялалын өмнө мартагдаж, цуглуулагч
 *    Оросод дугааргүй үлдэнэ — тэнд нөхөх боломжгүй. Тиймээс апп
 *    үлдэгдэл багасахад өөрөө шинэ муж авна.
 */
export const SerialBlockInput = z.object({
    count: z.number().int().min(1).max(20_000).default(10_000),
});
export const SerialBlockOutput = z.object({
    id: Uuid,
    /** Бүтэн он — дугаар нь `YY` + 7 орон тул он солигдвол шинэ муж хэрэгтэй. */
    year: z.number().int(),
    firstSeq: z.number().int(),
    lastSeq: z.number().int(),
    issuedAt: IsoDate,
});
// ============================================================
//  Гар утасны нэвтрэлт — сесс
//
//  Вэбийн cookie урсгалаас БҮРЭН ТУСДАА. Refresh token нь body-д явж,
//  аппын SecureStore-д хадгалагдана — `typ: 'device-refresh'` тул
//  вэбийн токентой СОЛИГДОХГҮЙ.
//
//  ⚠️ Идэвхжүүлэх токен БАЙХГҮЙ. Ажилтан зөвхөн хэрэглэгчийн нэр +
//     нууц үгээрээ нэвтэрнэ; төхөөрөмжийн мөрийг сервер өөрөө
//     бүртгэнэ (docs/ANDROID.md §2).
// ============================================================
/**
 * Аппын нэвтрэлт. Ажилтны оруулах зүйл ЗӨВХӨН нэр + нууц үг.
 *
 * `deviceId` нь ажилтны шивдэг зүйл БИШ — өмнөх нэвтрэлтэд серверээс
 * ирсэн утгыг апп SecureStore-оос уншиж илгээнэ. Байхгүй бол (шинэ
 * суулгалт) сервер шинэ төхөөрөмжийн мөр үүсгэнэ.
 */
export const DeviceLoginInput = z.object({
    username: z.string().min(3).max(50),
    password: z.string().min(6).max(200),
    /** Өмнөх нэвтрэлтийн `device.id` — SecureStore-оос. Эхний удаад байхгүй. */
    deviceId: Uuid.optional(),
    /** Утасны загвар (`Sunmi L2Ks`) — шинэ мөрийн шошго болно. Хувийн мэдээлэл БИШ. */
    deviceName: z.string().max(150).optional(),
    appVersion: z.string().max(50).optional(),
    osVersion: z.string().max(50).optional(),
});
export const DeviceRefreshInput = z.object({
    refreshToken: z.string().min(20),
    appVersion: z.string().max(50).optional(),
    osVersion: z.string().max(50).optional(),
});
/** Сесс нь access токены `sid` claim-аас олдоно — body шаардлагагүй. */
export const DeviceLogoutInput = z.object({}).optional();
/**
 * Нийтэлсэн APK — bucket-ийн `releases/latest.json` (docs/ANDROID.md §7.4).
 * Бичдэг нь `services/core/src/scripts/publish-release.ts` л.
 *
 * `md5` — утас татсан файлаа үүгээр шалгана (expo-file-system нь зөвхөн
 * MD5-ыг native-аар тоолдог). `sha256` — хүн гараар шалгахад.
 */
export const MobileRelease = z.object({
    versionCode: z.number().int().positive(),
    versionName: z.string().min(1).max(20),
    bytes: z.number().int().positive(),
    md5: z.string().regex(/^[0-9a-f]{32}$/),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    notes: z.string().max(2000),
    commit: z.string().max(40),
    publishedAt: IsoDate,
});
/** Аппын горим — сервер роль + төхөөрөмжийн төрлөөс гаргана, апп өөрөө сонгохгүй. */
export const AppMode = z.enum(['collector', 'warehouse', 'store']);
export const DeviceContextOutput = z.object({
    id: Uuid,
    code: DeviceCode,
    type: DeviceType,
    label: z.string(),
    storeId: Uuid.nullable(),
});
/**
 * Админд харагдах идэвхтэй сесс — «хэн, хэзээнээс, хэзээ дуусах».
 * ⚠️ Токен, jti ХЭЗЭЭ Ч энд орохгүй.
 */
export const DeviceSessionSummary = z.object({
    id: Uuid,
    userId: Uuid,
    username: z.string(),
    fullName: z.string(),
    issuedAt: IsoDate,
    lastUsedAt: IsoDate,
    expiresAt: IsoDate,
    appVersion: z.string().nullable(),
});
/** ⚠️ Вэбийн LoginOutput-оос ТУСДАА: refreshToken нь cookie БИШ, body-д явна. */
export const DeviceSessionOutput = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    refreshToken: z.string(),
    refreshExpiresIn: z.number().int(),
    sessionId: Uuid,
    user: AuthUserOutput,
    device: DeviceContextOutput,
    mode: AppMode,
});
/** Шинэ хувилбар. Хуучныг ЗАСАХГҮЙ — 10 жилийн баталгааны нотолгоо. */
export const WarrantyTermsCreateInput = z.object({
    version: z.string().min(1).max(50),
    bodyMn: z.string().min(10),
    bodyRu: z.string().min(10).nullable().optional(),
    effectiveFrom: IsoDate,
});
export const CommissionRuleCreateInput = z.object({
    storeId: Uuid.nullable().optional(),
    role: UserRole.optional(),
    percent: z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, {
        message: 'Хувь "2.50" хэлбэртэй string байна',
    }),
    effectiveFrom: IsoDate,
});
export const CommissionEffectiveQuery = z.object({
    storeId: Uuid.optional(),
    date: IsoDate.optional(),
});
/** Ханшийг гараар оруулах — Монголбанкны cron ажиллаагүй үед. */
export const FxCreateInput = z.object({
    currency: z.string().length(3).default('RUB'),
    date: IsoDate,
    rate: RateString,
    source: z.string().max(50).default('manual'),
});
/**
 * Ролийн эрхийг асаах/унтраах — НЭГ буюу ОЛОН зэрэг.
 *
 * Олныг зэрэг авах болсон шалтгаан: админ «энэ модулийн бүгдийг
 * асаа» гэж дардаг ба тэр нь 6 тусдаа хүсэлт болбол дунд нь тасарч
 * хагас хадгалагдах эрсдэлтэй. Нэг хүсэлт = нэг транзакц.
 *
 * ⚠️ `permissions[]` нь `"модуль.үйлдэл"` — жагсаалт `@boots/domain`-д
 *    (`ALL_PERMISSIONS`). Энд `z.enum` бичихгүй: каталог өсөх бүрд
 *    schema-г засах шаардлагагүй байх ёстой. Мэдэгдэхгүй түлхүүрийг
 *    route нь `isKnownPermission`-оор шалгаж 400 өгнө.
 */
export const PermissionUpdateInput = z.object({
    role: UserRole,
    permissions: z.array(z.string().min(3).max(64)).min(1).max(200),
    allowed: z.boolean(),
});
/** Ролийг бүхэлд нь кодын анхдагч руу буцаана — зөрүү бүгд устана. */
export const PermissionResetInput = z.object({
    role: UserRole,
});
export const FxGapsQuery = z.object({
    currency: z.string().length(3).default('RUB'),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
export const AuditQuery = Pagination.extend({
    entityType: z.string().max(50).optional(),
    entityId: Uuid.optional(),
    userId: Uuid.optional(),
    action: z.string().max(50).optional(),
});
// ============================================================
//  Алдааны хариу — бүх сервис нэг хэлбэр
// ============================================================
export const ErrorCode = z.enum([
    'VALIDATION_ERROR', 'UNAUTHENTICATED', 'DEVICE_BLOCKED', 'FORBIDDEN',
    'NOT_FOUND', 'CONFLICT', 'VERSION_CONFLICT', 'BUSINESS_RULE',
    'RATE_LIMITED', 'EBARIMT_UNAVAILABLE', 'ASSISTANT_UNAVAILABLE', 'INTERNAL',
]);
export const ErrorResponse = z.object({
    error: z.object({
        code: ErrorCode,
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
/**
 * Орлогын график — `GET /reports/revenue`.
 *
 * ⚠️ Муж бүрийн хугацааны хил, bucket-ийн жагсаалтыг `@boots/domain`
 *    дахь `revenueWindow()` тооцно. Энд зөвхөн оролтын баталгаажуулалт.
 */
export const RevenueQuery = z.object({
    range: z.enum(['day', 'week', 'month', 'year']).default('week'),
    storeId: Uuid.optional(),
});
// ============================================================
//  Нягтлан бодогч (2026-10-15) — `/accounting/*`
// ============================================================
/** УБ-ийн өдөр `YYYY-MM-DD`; `to` тэр өдрийг ОРУУЛНА. */
const UbDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Огноо YYYY-MM-DD' });
export const AccountingRangeQuery = z.object({
    from: UbDay.optional(),
    to: UbDay.optional(),
    storeId: Uuid.optional(),
    supplierId: Uuid.optional(),
});
export const PriceChangesQuery = AccountingRangeQuery.omit({ supplierId: true }).extend({
    groupBy: z.enum(['day', 'month', 'year']).default('day'),
});
/** Размерын сонголтыг клиент шүүнэ — сервер бүх размерыг нэг дор буцаана. */
export const StockBySizeQuery = z.object({ storeId: Uuid.optional() });
//# sourceMappingURL=index.js.map